import React, { FC, useEffect } from 'react'
import LoginFormBody from '../common/LoginFormBody/LoginFormBody'
import AppFormControl from '../../../components/AppForm/AppFormControl'
import InputLabel from '@mui/material/InputLabel'
import { Controller, useForm } from 'react-hook-form'
import { FormHelperText } from '@mui/material'
import Button from '@mui/material/Button'
import { regAuthenticator } from '../../../store/reducers/auth/actions'
import { useTypedSelector } from '../../../hooks/useTypedSelector'
import {
	selectAuthenticatorId,
	selectMfaToken,
	selectOobChannel,
} from '../../../store/reducers/auth/selectors'
import { history } from '../../../store'
import { PAGE_LOGIN, PAGE_LOGIN_2FA_CODE } from '../../../constants/navigation'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import parsePhoneNumber, { isPossiblePhoneNumber } from 'libphonenumber-js'
import { useDefaultComponentTools } from '../../../hooks/useDefaultComponentTools'

interface ILogin2FAPhoneForm {
	phone: string
}

const PHONE_FIELD_NAME = 'phone'

const Login2FAPhoneForm: FC = () => {
	const mfaToken = useTypedSelector(selectMfaToken)
	const oobChannel = useTypedSelector(selectOobChannel)
	const authenticatorId = useTypedSelector(selectAuthenticatorId)
	const { dispatch, alert } = useDefaultComponentTools()
	const {
		handleSubmit,
		control,
		getValues,
		formState: { errors },
	} = useForm<ILogin2FAPhoneForm>({
		mode: 'all',
		defaultValues: {
			[PHONE_FIELD_NAME]: '',
		},
	})

	useEffect(() => {
		if (!oobChannel || oobChannel !== 'sms' || authenticatorId) {
			history.replace(PAGE_LOGIN)
		}
	}, [oobChannel, authenticatorId])

	const formValues = getValues()
	const phoneError = errors[PHONE_FIELD_NAME]?.message || ''
	const isSubmitButtonDisabled = !formValues[PHONE_FIELD_NAME] || !!phoneError

	const onSubmit = async (data: ILogin2FAPhoneForm): Promise<void> => {
		try {
			if (!mfaToken) {
				alert.error('Invalid mfa token.')

				return
			}

			const phoneE164 = `+${data.phone}`

			await dispatch(regAuthenticator(['sms'], phoneE164, mfaToken))

			history.replace(PAGE_LOGIN_2FA_CODE)
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		}
	}

	return (
		<LoginFormBody
			title={'Login'}
			subTitle={'Please enter your phone number to enroll'}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<AppFormControl
					sx={{ mb: 9, mt: 3 }}
					color={'primary'}
					error={!!phoneError}
				>
					<InputLabel shrink htmlFor={'login-email'}>
						Phone number
					</InputLabel>

					<Controller
						control={control}
						rules={{
							required: 'Invalid phone number',
							validate: {
								phoneNumber: value => {
									const phoneNumber = parsePhoneNumber(
										`+${value}`
									)

									if (phoneNumber) {
										return (
											isPossiblePhoneNumber(
												phoneNumber.number,
												phoneNumber.country
											) || 'Invalid phone number'
										)
									}

									return 'Invalid phone number'
								},
							},
						}}
						render={({ field }) => (
							<PhoneInput
								{...field}
								country={'us'}
								enableAreaCodes
								enableLongNumbers
								placeholder={''}
								onChange={value => {
									field.onChange(value)
								}}
							/>
						)}
						name={PHONE_FIELD_NAME}
					/>

					{!!phoneError && (
						<FormHelperText id={'2fa-tel'} sx={{ top: '5rem' }}>
							{phoneError}
						</FormHelperText>
					)}
				</AppFormControl>

				<Button
					size={'large'}
					variant='contained'
					type={'submit'}
					color={'primary'}
					disabled={isSubmitButtonDisabled}
					sx={{ mr: 'auto', maxWidth: '5.625rem' }}
				>
					Enroll
				</Button>
			</form>
		</LoginFormBody>
	)
}

export default Login2FAPhoneForm
